import { colorUtils, positionUtils } from '../../utils';
import {
  createColorPickerStyleService,
  createFontPickerStyleService,
  TpaStyleService,
  type ResponseColorData,
} from '../../services';

import type {
  PlatformApiMethods,
  BuilderFontPickerOnFontChange,
} from '@wix/editor-platform-host-integration-apis';
import type {
  AppData,
  DocumentServicesObject,
  BuilderFontStyleParam,
} from '@wix/document-services-types';
import type {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
} from '../../types/tpaMessage';
import _ from 'lodash';
import { IExperimentsApiAdapter } from '../../../../utils/experimentsApiAdapter';
import { FontPickerOnStyleChangePayload } from '@wix/editor-platform-sdk-types';
import {
  convertFontPresetToGlobalVariable,
  convertLegacyColorKeyToWixGlobalVariable,
} from '../../utils/globalVariablesUtils';
import { mapBuilderFontStyleToTpaFontParam } from '../utils/legacyMappers';
import { stringAsCssVar } from '../../../../../utils';
import { parseFontValue } from '../../../editActionsServices/nativeActions/globalDesign/text/parser';

export interface EditorUILibHandlers {
  openColorPicker: TPAHandler;
  openFontPicker: TPAHandler;
  openBackgroundPicker: TPAHandler;
}

export type UILibHandlerType = keyof EditorUILibHandlers;

export type ValueFromFontPicker = string | number | boolean;

interface UiLibHandlersServicesDependencies {
  tpaStyleService: TpaStyleService;
}

const defaultColorPickerValue = '#ffffff';

const isPickerFontGlobalVariable = (
  documentServicesAPI: DocumentServicesObject,
  pickerFont: Parameters<BuilderFontPickerOnFontChange>[0],
) => {
  if (!pickerFont.cssVarName) {
    return false;
  }

  const list = documentServicesAPI.global.variables
    .list({ type: 'FontVar' })
    .map(documentServicesAPI.global.variables.get);
  const variable = list.find((v) => v.cssVarName === pickerFont.cssVarName);

  if (!variable || typeof variable.value.value !== 'string') {
    return false;
  }

  const parsedPickerFont = parseFontValue(pickerFont.value);
  const parsedGlobalFont = parseFontValue(variable.value.value);
  const { fontFamily: pickerFamily, ...pickerRest } = parsedPickerFont;
  const { fontFamily: globalFamily, ...globalRest } = parsedGlobalFont;
  return (
    _.isEqual(pickerRest, globalRest) &&
    // Builder font picker always suffixes the font family with 'sans-serif' while global variables don't
    pickerFamily?.includes(globalFamily ?? '')
  );
};

export const createEditorUILibHandlers = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  { tpaStyleService }: UiLibHandlersServicesDependencies,
): EditorUILibHandlers => {
  const colorPickerStyleService =
    createColorPickerStyleService(documentServicesAPI);
  const fontPickerStyleService = createFontPickerStyleService(
    documentServicesAPI,
    experimentsAPI,
  );

  const openColorPicker = (msg: TPAMessage, callback: TPAMessageCallback) => {
    const pickerPosition = positionUtils.getTpaSettingsPositionRelativeToEditor(
      {
        top: msg.data?.top,
        left: msg.data?.left,
      },
    );
    const wixParam = msg.data?.wixParam;
    const { compId } = msg;
    const initialValue = msg.data?.color || defaultColorPickerValue;

    const onClose = () =>
      callback({
        panelClose: true,
      });

    if (platformApiMethods.editor.selectColor) {
      const onStyleChange = (color: string) => {
        if (wixParam) {
          colorPickerStyleService.onStyleChange(
            compId,
            wixParam,
            color,
            callback,
            tpaStyleService.setStyleParam,
          );
        } else {
          colorPickerStyleService.onStyleChangeWithoutWixParam(
            { color, cssVariableTheme: '', isHover: false, theme: '' },
            callback,
          );
        }
      };

      const extractColor = (): string => {
        const isHex =
          colorUtils.isHexColor(initialValue) ||
          colorUtils.isHexaColor(initialValue);
        const isRgb = colorUtils.isRgbColor(initialValue);

        if (isHex || isRgb) {
          return initialValue;
        }

        if (
          typeof initialValue === 'string' &&
          initialValue.startsWith('color_')
        ) {
          const foundValue =
            convertLegacyColorKeyToWixGlobalVariable(initialValue);
          if (foundValue) {
            return stringAsCssVar(foundValue);
          }
        }

        return initialValue;
      };

      platformApiMethods.editor.selectColor({
        initialValue: extractColor(),
        mode: ['solid', 'gradient'],
        position: pickerPosition,
        onClose,
        onChange: onStyleChange,
        onApply: onStyleChange,
        showBrand: true,
        /**
         * Allow picking colors that aren't saved in brand panel as custom
         * colors (custom tab)
         */
        showCustom: true,
        /** Hide colors that are saved in site brand panel (brand tab) */
        hideCustomColors: true,
        /** Show advanced settings button in color picker */
        showAdvancedSettings: true,
      });

      return;
    }

    const getOnChange = () => {
      if (wixParam) {
        return (newColorData: ResponseColorData) =>
          colorPickerStyleService.onStyleChange(
            compId,
            wixParam,
            newColorData.theme || newColorData.color,
            callback,
            tpaStyleService.setStyleParam,
          );
      }

      return (newColorData: ResponseColorData) =>
        colorPickerStyleService.onStyleChangeWithoutWixParam(
          newColorData,
          callback,
        );
    };

    platformApiMethods.editor.openColorPicker(
      {} as AppData,
      '',
      {
        overridePosition: true,
        position: pickerPosition,
        color: initialValue,
        onClose,
      },
      getOnChange(),
    );
  };

  const openFontPicker = (msg: TPAMessage, callback: TPAMessageCallback) => {
    const wixParam = msg.data?.wixParam;
    const { compId } = msg;
    const pickerPosition = positionUtils.getTpaSettingsPositionRelativeToEditor(
      {
        top: msg.data?.top,
        left: msg.data?.left,
      },
    );

    const getComponentStyle = () => {
      if (wixParam) {
        const compStyle: Record<string, any> =
          documentServicesAPI.tpa.style.get(compId).style?.fonts[wixParam];
        compStyle.htmlTag = compStyle.htmlTag ?? msg?.data?.htmlTag;
        return compStyle;
      }

      return _.isPlainObject(msg.data?.value) ? msg.data?.value : {};
    };

    const getOnChange = (compStyle: any) => {
      const extractOnChangePayload = (
        styles: any[],
      ): FontPickerOnStyleChangePayload => {
        if (styles.length > 1) {
          return styles as FontPickerOnStyleChangePayload;
        }

        return styles[0];
      };

      if (wixParam) {
        return (...styles: any[]) => {
          const payload = extractOnChangePayload(styles);
          fontPickerStyleService.onStyleChange(
            compId,
            compStyle,
            wixParam,
            payload,
            callback,
            tpaStyleService.setStyleParam,
          );
        };
      }

      return (...styles: any[]) => {
        const payload = extractOnChangePayload(styles);
        fontPickerStyleService.onStyleChangeWithoutWixParam(
          compStyle,
          callback,
          payload,
        );
      };
    };

    const componentStyle = getComponentStyle();
    const onStyleChange = getOnChange(componentStyle);

    const getPanelSectionDefinitionValue = (
      shouldShow?: boolean,
    ): 'enabled' | 'hidden' => (shouldShow ? 'enabled' : 'hidden');

    const options = {
      position: pickerPosition,
      title: msg.data?.title,
      ...(msg.data?.fontMaxSize && { fontMaxSize: msg.data?.fontMaxSize }),
      ...(msg.data?.fontMinSize && { fontMinSize: msg.data?.fontMinSize }),
      panelSectionsDefinition: {
        theme: getPanelSectionDefinitionValue(!msg.data?.hideTheme),
        font: getPanelSectionDefinitionValue(!msg.data?.hideFont),
        size: getPanelSectionDefinitionValue(!msg.data?.hideSize),
        style: getPanelSectionDefinitionValue(!msg.data?.hideStyle),
        htmlTag: getPanelSectionDefinitionValue(msg.data?.showHTMLTag),
        fontMaxSize: msg.data?.fontMaxSize,
        fontMinSize: msg.data?.fontMinSize,
      },
      componentStyle,
      overridePositon: true,
    };

    if (
      platformApiMethods.editor.openBuilderFontPicker &&
      platformApiMethods.editor.selectColor
    ) {
      const onBuilderFontChange: BuilderFontPickerOnFontChange = (
        fontStyle,
        fieldAccesibility,
      ) => {
        const fontToMap = {
          ...fontStyle,
          ...fieldAccesibility,
          cssVarName: isPickerFontGlobalVariable(documentServicesAPI, fontStyle)
            ? fontStyle.cssVarName
            : undefined,
        };

        const tpaFontParam = mapBuilderFontStyleToTpaFontParam(
          documentServicesAPI,
          fontToMap,
        );

        if (!wixParam) {
          callback({ fontParam: tpaFontParam });
          return;
        }

        const compRef = documentServicesAPI.components.get.byId(compId);

        if (!compRef) {
          throw new Error(`No compRef found for compId: ${compId}`);
        }
        const styleParams: BuilderFontStyleParam = {
          type: 'font',
          key: wixParam,
          param: {
            value: {
              builderFontStyle: true,
              name: fontStyle.name,
              value: fontStyle.value,
              cssVarName: fontStyle.cssVarName,
              textDecoration: fontStyle.textDecoration,
              htmlTag: fieldAccesibility.htmlTag,
            },
          },
        };

        tpaStyleService.setStyleParam(compRef, styleParams, () => {
          callback({ fontParam: tpaFontParam });
        });
      };
      const globalVariable = convertFontPresetToGlobalVariable(
        options.componentStyle.preset,
      );
      platformApiMethods.editor.openBuilderFontPicker(
        {} as AppData,
        '',
        {
          ...options,
          componentStyle: {
            ...options.componentStyle,
            ...options.componentStyle.style,
            theme: globalVariable,
            cssVariableName: globalVariable,
          },
        },
        () => {},
        onBuilderFontChange,
      );
      return;
    }

    platformApiMethods.editor.openFontPicker(
      {} as AppData,
      '',
      options,
      onStyleChange,
    );
  };

  const openBackgroundPicker = (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const color = msg.data?.color || defaultColorPickerValue;
    const pickerPosition = positionUtils.getTpaSettingsPositionRelativeToEditor(
      {
        top: msg.data?.top,
        left: msg.data?.left,
      },
    );

    const options = {
      color,
      position: pickerPosition,
      onChange: callback,
      onClose: () =>
        callback({
          panelClose: true,
        }),
    };

    platformApiMethods.editor.openBackgroundPicker?.(
      {} as AppData,
      '',
      options,
    );
  };

  return {
    openColorPicker,
    openFontPicker,
    openBackgroundPicker,
  };
};
